// shuffle method for random exercise display (Fisher–Yates shuffle)

import uniqueId from 'lodash/uniqueId';
import cloneDeep from 'lodash/cloneDeep';
import { hasOwnProperty } from '../screens/HomeTherapist/helpers';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logError } from './logHandling';

// source http://bost.ocks.org/mike/shuffle/
function shuffle(array) {
  // Check if the input array is defined and is an array
  if (!Array.isArray(array)) {
    throw new Error('Input is not a valid array');
  }

  let currentIndex = array.length,
    temporaryValue,
    randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // Check if array elements are defined at currentIndex and randomIndex
    if (array[currentIndex] !== undefined && array[randomIndex] !== undefined) {
      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }
  }

  return array;
}

// function to reduce array into smaller chunks
function chunkExercises(arr, len) {
  let chunks = [],
    i = 0,
    n = arr.length;
  while (i < n) {
    chunks.push(shuffle(arr.slice(i, (i += len))));
  }

  // if more than one chunk is created and the last chunk contains only 1 element, merge with first chunk
  if (chunks.length > 1 && chunks[chunks.length - 1].length === 1) {
    let lastChunk = chunks.pop()[0];
    chunks[0].push(lastChunk);
  }

  return chunks;
}

// eslint-disable-next-line no-extend-native
String.prototype._shuffle = function () {
  let array = this.split(''),
    length = array.length;
  let i = length - 1;
  while (i > 0) {
    let random = Math.floor(Math.random() * (i + 1));
    let temp = array[i];
    array[i] = array[random];
    array[random] = temp;
    i--;
  }
  return array.join('');
};

export function prepareJson(wordsetId, localWordset, settingsParam) {
  let settings;
  const json = cloneDeep(localWordset.preparedSet);
  // if settings are passed its Eigentraining otherwise usual therapist app exercises
  if (typeof settingsParam === 'undefined') {
    settings = localWordset.exerciseSettings;
  } else {
    settings = { ...settingsParam };
  }
  let exercises = json.exercises;
  const output = [];
  let id = 0;
  // shuffle exercise order
  shuffle(exercises);

  // group exercises by their type
  let groupedExercises = exercises.reduce(function (r, a) {
    r[a.type] = r[a.type] || [];
    r[a.type].push(a);
    return r;
  }, Object.create(null));

  for (let key in groupedExercises) {
    const group = groupedExercises[key];
    groupedExercises[key] = chunkExercises(group, 4);
  }

  // add chunks in sequential order
  exercises = [];
  for (let i = 0; i < groupedExercises.Verstehen.length; i++) {
    exercises.push(groupedExercises.Schreiben[i]);
    exercises.push(groupedExercises.Lesen[i]);
    exercises.push(groupedExercises.Sprechen[i]);
    exercises.push(groupedExercises.Verstehen[i]);
  }

  // reduce exercise chunks to exercises
  exercises = [].concat.apply([], exercises);

  //Loop to delete disabled exercisese and prepare exercises
  for (let index = exercises.length - 1; index >= 0; index--) {
    const condition =
      (exercises[index].type == 'Schreiben' &&
        settings.Schreiben.active == false) ||
      (exercises[index].type == 'Lesen' && settings.Lesen.active == false) ||
      (exercises[index].type == 'Sprechen' &&
        settings.Sprechen.active == false) ||
      (exercises[index].type == 'Verstehen' &&
        settings.Verstehen.active == false);
    if (condition) {
      delete exercises[index];
    } else {
      if (!hasOwnProperty(exercises[index], 'exerciseClass')) {
        exercises[index].exerciseClass = 'word';
      }
      exercises[index].id = id;

      // exercise creation for words and sentences is done here
      if (exercises[index].exerciseClass !== 'text') {
        let possible = 'abcdefghijklmnopqrstuvwxyzöüäß';
        if (
          exercises[index].exerciseClass === 'word' &&
          exercises[index].word.match(/[1234567890:]/g) &&
          exercises[index].word.match(/[1234567890:]/g).length > 1
        ) {
          possible = '1234567890';
        }

        // if Schreiben exercise push shuffled value of word to json.
        if (
          exercises[index].type == 'Schreiben' &&
          settings.Schreiben.setting == 'anagram'
        ) {
          exercises[index].shuffle = exercises[index].word;

          for (let i = 0; i < settings.Schreiben.value; i++) {
            exercises[index].shuffle += possible.charAt(
              Math.floor(Math.random() * possible.length),
            );
          }
          exercises[index].shuffle = exercises[index].shuffle._shuffle();
          exercises[index].name = 'Schreiben';
        } else if (
          exercises[index].type == 'Schreiben' &&
          settings.Schreiben.setting == 'gap'
        ) {
          exercises[index].shuffle = exercises[index].word;
          exercises[index].gap = [];

          if (!settings.Schreiben.value || settings.Schreiben.value === 0) {
            settings.Schreiben.value = 1;
          }

          if (settings.Schreiben.value == 1) {
            //Add Graphem if gap setting is 1 so exercise makes sense
            let possibleMinusWord = possible;
            for (let j = 0; j < exercises[index].shuffle.length; j++) {
              possibleMinusWord = possibleMinusWord.replace(
                exercises[index].shuffle[j].toLowerCase(),
                '',
              );
            }
            exercises[index].shuffle += possibleMinusWord.charAt(
              Math.floor(Math.random() * possibleMinusWord.length),
            );
          }

          for (
            let iterator = 0;
            iterator < exercises[index].word.length;
            iterator++
          ) {
            exercises[index].gap[iterator] = false;
          }

          for (
            let iterator = 0;
            iterator <
            exercises[index].word.replace(/ /g, '').length -
              settings.Schreiben.value;
            iterator++
          ) {
            let randomindex = Math.floor(
              Math.random() * exercises[index].gap.length,
            );
            // if random chosen index is a blank space or is already chosen, try again!
            if (
              exercises[index].gap[randomindex] === true ||
              exercises[index].word[randomindex] === ' '
            ) {
              iterator--;
            } else {
              exercises[index].gap[randomindex] = true;
            }
          }
          let lettersdeleted = 0;
          for (
            let iterator = 0;
            iterator < exercises[index].gap.length;
            iterator++
          ) {
            if (exercises[index].gap[iterator] == true) {
              exercises[index].shuffle =
                exercises[index].shuffle.slice(0, iterator - lettersdeleted) +
                exercises[index].shuffle.slice(iterator + 1 - lettersdeleted);
              lettersdeleted++;
            }
          }

          exercises[index].shuffle = exercises[index].shuffle._shuffle();
          exercises[index].name = 'Schreiben';
        } else if (
          exercises[index].type == 'Schreiben' &&
          settings.Schreiben.setting == 'full'
        ) {
          exercises[index].name = 'Schreiben';
          //if exercise contains more then two numbers or number and : then display the special number keyboard
          if (
            exercises[index].word.match(/[1234567890:]/g) &&
            exercises[index].word.match(/[1234567890:]/g).length > 1
          ) {
            exercises[index].shuffle = '1234567890:';
          } else if (
            exercises[index].word[0] === exercises[index].word[0].toUpperCase()
          ) {
            //if first letter is uppercase insert Capital into full writing editor
            exercises[index].shuffle = 'QWERTZUIOPÜASDFGHJKLÖÄYXCVBNMß-';
          } else {
            exercises[index].shuffle = 'qwertzuiopüasdfghjklöäyxcvbnmß-';
          }
        } else if (exercises[index].type == 'Lesen') {
          exercises[index].name = 'Lesen';
          exercises[index].order = [4, 3, 2, 1];
          exercises[index].order = shuffle(exercises[index].order);
          shuffle(exercises[index].related);
          shuffle(exercises[index].unrelated);
        } else if (exercises[index].type == 'Verstehen') {
          exercises[index].name = 'Verstehen';
          exercises[index].order = [4, 3, 2, 1];
          exercises[index].order = shuffle(exercises[index].order);
          shuffle(exercises[index].related);
          shuffle(exercises[index].unrelated);
        } else if (exercises[index].type == 'Sprechen') {
          exercises[index].name = 'Sprechen';
        }
      }
      // else (if is text) add text exercise
      // else {
      // }

      // add unique index to every exercise by setting a timestamp in ms to ensure re-rendering the components when they appear twice in a row.
      exercises[index].uniqueExercisePrepareDate =
        new Date().getTime() + uniqueId('__audio__cache');
      output.push(exercises[index]);
      id++;

      (async () => {
        try {
          const allKeys = await AsyncStorage.getAllKeys();
          const recordingKeys = allKeys.filter((key) =>
            key.includes('@recording:'),
          );
          await AsyncStorage.multiRemove(recordingKeys);
        } catch (_error) {
          logError('ERROR while removing the cached recording keys');
        }
      })();
    }
  }
  //Display Exercises in random order
  return output;
}
